import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { de } from './dictionaries/de'
import { en } from './dictionaries/en'
import { detectInitialLocale, persistLocale } from './locale'
import type { Locale, TranslationKey, TranslationParams, TranslationShape } from './types'

type Dictionary = TranslationShape<typeof en>
export type I18nKey = TranslationKey

type I18nContextValue = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: I18nKey, params?: TranslationParams) => string
}

const dictionaries: Record<Locale, Dictionary> = { en, de }

const I18nContext = createContext<I18nContextValue | null>(null)

function getByPath(dict: Record<string, unknown>, key: string): string | undefined {
  const segments = key.split('.')
  let cursor: unknown = dict

  for (const segment of segments) {
    if (!cursor || typeof cursor === 'string' || typeof cursor !== 'object' || !(segment in cursor)) {
      return undefined
    }

    cursor = (cursor as Record<string, unknown>)[segment]
  }

  return typeof cursor === 'string' ? cursor : undefined
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key]
    return value === undefined ? `{${key}}` : String(value)
  })
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<Locale>(detectInitialLocale)

  useEffect(() => {
    document.documentElement.lang = locale
    persistLocale(locale)
  }, [locale])

  const t = useCallback((key: I18nKey, params?: TranslationParams) => {
    const localized = getByPath(dictionaries[locale], key)
    const fallback = localized ?? getByPath(dictionaries.en, key) ?? key
    return interpolate(fallback, params)
  }, [locale])

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useLocale() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useLocale must be used within I18nProvider')
  }

  return {
    locale: context.locale,
    setLocale: context.setLocale,
  }
}

export function useT() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useT must be used within I18nProvider')
  }

  return context.t
}
